import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  createEmergencyRequest,
  EMERGENCY_TYPES,
  getEmergencyRequests,
} from '../../services/docuvaultEmergencyService';
import { getSession } from '../../api';

const {
  FiX, FiAlertTriangle, FiShield, FiUser, FiMail, FiEdit3,
  FiCheck, FiLock, FiInfo, FiClock, FiFileText, FiChevronRight,
} = FiIcons;

const ROLES = ['executor', 'beneficiary', 'attorney', 'trustee', 'co-trustee', 'family member', 'other'];

function SignaturePreview({ text }) {
  return (
    <div className="relative h-16 bg-white rounded-2xl border-2 border-dashed border-accent-gold/40 flex items-center justify-center overflow-hidden">
      <div className="absolute bottom-4 left-6 right-6 h-px bg-accent-navy/10" />
      <AnimatePresence mode="wait">
        {text.trim() ? (
          <motion.p
            key={text}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-xl text-accent-navy px-6 pb-1 truncate max-w-full"
            style={{ fontFamily: "'Dancing Script', 'Brush Script MT', cursive", letterSpacing: '0.03em' }}
          >
            {text}
          </motion.p>
        ) : (
          <p className="text-sm text-text-muted/50 font-light italic select-none">
            Your signature will appear here…
          </p>
        )}
      </AnimatePresence>
    </div>
  );
}

const DocuVaultEmergencyReleaseModal = ({ isOpen, onClose, document: doc, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [emergencyType, setEmergencyType] = useState('');
  const [description, setDescription] = useState('');
  const [supportingEvidence, setSupportingEvidence] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('executor');
  const [relationship, setRelationship] = useState('');
  const [typedSig, setTypedSig] = useState('');
  const [sigConfirmed, setSigConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const user = getSession();
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
    // Check for existing pending requests on this doc
    const existing = getEmergencyRequests(doc?.id, 'pending');
    if (existing.length > 0) {
      setError('A pending emergency release request already exists for this document. The owner will review it shortly.');
    }
  }, [isOpen, doc?.id]);

  const canSubmit = emergencyType && description.trim().length >= 10
    && name.trim().length >= 2 && email.includes('@')
    && typedSig.trim().length >= 2 && sigConfirmed && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) {
      if (!emergencyType) setError('Please select the type of emergency.');
      else if (description.trim().length < 10) setError('Please provide a more detailed description (at least 10 characters).');
      else if (!sigConfirmed) setError('Please confirm your digital signature.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const req = createEmergencyRequest(
        doc,
        { name: name.trim(), email: email.trim(), role, relationship: relationship.trim() },
        emergencyType,
        description.trim(),
        typedSig.trim(),
        supportingEvidence.trim(),
      );
      setResult(req);
      setDone(true);
      setTimeout(() => { onSuccess?.(); handleClose(); }, 2800);
    } catch (e) {
      setError(e.message || 'Failed to submit emergency request.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1); setEmergencyType(''); setDescription(''); setSupportingEvidence('');
    setName(''); setEmail(''); setRole('executor'); setRelationship('');
    setTypedSig(''); setSigConfirmed(false); setSubmitting(false);
    setDone(false); setError(''); setResult(null);
    onClose();
  };

  if (!isOpen || !doc) return null;

  const steps = ['Emergency Type', 'Details', 'Identity & Signature'];

  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4 bg-accent-navy/70 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark max-h-[92vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-red-900 via-accent-navy to-[#1a2a5e] flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-red-500/30 text-white rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiAlertTriangle} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-serif text-white">Emergency Release Request</h3>
              <p className="text-white/50 text-[10px] font-black uppercase tracking-widest">
                Expedited Document Access · Cryptographically Logged
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 text-white/60 hover:text-white rounded-xl transition-colors">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Warning banner */}
        {!done && (
          <div className="px-6 py-3 bg-red-50 border-b border-red-100 flex items-start gap-2 flex-shrink-0">
            <SafeIcon icon={FiInfo} className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-red-700 font-light leading-relaxed">
              Emergency release is an <strong>expedited path</strong> for urgent situations. False claims are
              logged and may have legal consequences. The document owner will be notified immediately.
            </p>
          </div>
        )}

        {/* Step indicator */}
        {!done && (
          <div className="flex items-center gap-0 px-8 py-4 border-b border-brand-light bg-brand-light/50 flex-shrink-0">
            {steps.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black transition-all ${
                    step > i + 1 ? 'bg-green-500 text-white' : step === i + 1 ? 'bg-red-500 text-white' : 'bg-brand-dark text-text-muted'
                  }`}>
                    {step > i + 1 ? <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <span className={`text-[10px] font-black uppercase tracking-widest ${step === i + 1 ? 'text-accent-navy' : 'text-text-muted'}`}>{s}</span>
                </div>
                {i < steps.length - 1 && <div className="flex-1 h-0.5 bg-brand-dark mx-3" />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-8">
          <AnimatePresence mode="wait">
            {/* Success state */}
            {done && result && (
              <motion.div key="done" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiCheck} className="w-10 h-10 text-green-500" />
                </div>
                <h4 className="text-2xl font-serif text-accent-navy mb-3">Request Submitted</h4>
                <p className="text-text-muted font-light mb-6 max-w-sm mx-auto">
                  Your emergency release request has been logged and the document owner has been notified.
                  You will receive a response as soon as the request is reviewed.
                </p>
                <div className="bg-brand-light rounded-2xl p-4 max-w-sm mx-auto text-left space-y-2">
                  <div className="flex justify-between text-[10px]">
                    <span className="font-black uppercase tracking-widest text-text-muted">Request ID</span>
                    <span className="font-mono text-accent-navy">#{result.id.slice(0, 12)}</span>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span className="font-black uppercase tracking-widest text-text-muted">Document</span>
                    <span className="font-bold text-accent-navy truncate max-w-[200px]">{result.docName}</span>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span className="font-black uppercase tracking-widest text-text-muted">Status</span>
                    <span className="font-black text-amber-600 uppercase">Pending Review</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 1: Emergency Type */}
            {!done && step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div className="p-3 bg-brand-light rounded-2xl border border-brand-dark flex items-center gap-3">
                  <SafeIcon icon={FiFileText} className="w-5 h-5 text-accent-navy flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Requesting access to</p>
                    <p className="text-sm font-bold text-accent-navy truncate">{doc.original_name}</p>
                  </div>
                </div>
                <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Select Emergency Type</p>
                <div className="space-y-2">
                  {Object.entries(EMERGENCY_TYPES).map(([key, cfg]) => (
                    <button
                      key={key}
                      onClick={() => { setEmergencyType(key); setError(''); }}
                      className={`w-full p-4 rounded-2xl border-2 text-left transition-all flex items-start gap-3 ${
                        emergencyType === key
                          ? 'border-red-400 bg-red-50'
                          : 'border-brand-dark hover:border-red-300 bg-white'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.bg} border ${cfg.border}`}>
                        <SafeIcon icon={FiAlertTriangle} className={`w-5 h-5 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-accent-navy">{cfg.label}</p>
                        <p className="text-[10px] text-text-muted font-light leading-snug mt-0.5">{cfg.description}</p>
                      </div>
                      {emergencyType === key && <SafeIcon icon={FiCheck} className="w-4 h-4 text-red-500 flex-shrink-0 mt-1" />}
                    </button>
                  ))}
                </div>
                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-700">
                    <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {error}
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 2: Details */}
            {!done && step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <div className="p-3 bg-red-50 rounded-2xl border border-red-100 flex items-start gap-2">
                  <SafeIcon icon={EMERGENCY_TYPES[emergencyType] ? FiAlertTriangle : FiInfo} className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] text-red-700 font-light leading-relaxed">
                    {EMERGENCY_TYPES[emergencyType]?.description || 'Please describe the emergency.'}
                  </p>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">
                    Emergency Description <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={4}
                    maxLength={2000}
                    placeholder="Describe the nature of the emergency and why immediate access to this document is required…"
                    className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm resize-none"
                  />
                  <p className="text-right text-[9px] text-text-muted mt-1">{description.length}/2000</p>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">
                    Supporting Evidence (Optional)
                  </label>
                  <textarea
                    value={supportingEvidence}
                    onChange={e => setSupportingEvidence(e.target.value)}
                    rows={2}
                    maxLength={1000}
                    placeholder="e.g. death certificate number, court case reference, hospital name…"
                    className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm resize-none"
                  />
                </div>
              </motion.div>
            )}

            {/* Step 3: Identity & Signature */}
            {!done && step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                {/* Identity */}
                <div className="space-y-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Your Identity</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                      <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="Full Legal Name"
                        className="w-full pl-11 pr-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm"
                      />
                    </div>
                    <div className="relative">
                      <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="Email Address"
                        className="w-full pl-11 pr-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={role}
                      onChange={e => setRole(e.target.value)}
                      className="px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm"
                    >
                      {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                    </select>
                    <input
                      type="text"
                      value={relationship}
                      onChange={e => setRelationship(e.target.value)}
                      placeholder="Relationship to owner (e.g. spouse)"
                      className="px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm"
                    />
                  </div>
                </div>

                {/* Signature */}
                <div className="space-y-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy flex items-center gap-1.5">
                    <SafeIcon icon={FiEdit3} className="w-3.5 h-3.5 text-accent-gold" />
                    Digital Signature <span className="text-red-400">*</span>
                  </p>
                  <div className="relative">
                    <SafeIcon icon={FiEdit3} className="absolute left-4 top-1/2 -translate-y-1/2 text-accent-gold w-4 h-4" />
                    <input
                      type="text"
                      value={typedSig}
                      onChange={e => setTypedSig(e.target.value)}
                      placeholder="Type your full name as your signature"
                      maxLength={120}
                      className="w-full pl-11 pr-4 py-3.5 bg-amber-50 border-2 border-accent-gold/40 focus:border-accent-gold rounded-2xl outline-none text-sm"
                      style={{ fontFamily: "'Dancing Script', 'Brush Script MT', cursive", fontSize: '1rem' }}
                    />
                  </div>
                  <SignaturePreview text={typedSig} />
                  <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-2xl border-2 bg-amber-50/60 border-accent-gold/20">
                    <div className="relative flex-shrink-0 mt-0.5">
                      <input type="checkbox" checked={sigConfirmed} onChange={e => setSigConfirmed(e.target.checked)} className="sr-only" />
                      <div
                        onClick={() => setSigConfirmed(v => !v)}
                        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all cursor-pointer ${
                          sigConfirmed ? 'bg-green-500 border-green-500' : 'bg-white border-accent-gold/50 hover:border-accent-gold'
                        }`}
                      >
                        {sigConfirmed && <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                    <p className="text-[10px] text-text-muted font-light leading-relaxed">
                      I, <strong className="text-accent-navy">{typedSig.trim() || '(your name)'}</strong>, confirm
                      this emergency request is truthful and made in good faith. I understand this request is
                      <strong className="text-accent-navy"> permanently logged, timestamped, and cryptographically recorded</strong>.
                    </p>
                  </label>
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-700">
                    <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {error}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        {!done && (
          <div className="p-6 border-t border-brand-light flex items-center justify-between flex-shrink-0 bg-brand-light/30">
            <button
              onClick={step === 1 ? handleClose : () => { setStep(s => s - 1); setError(''); }}
              className="px-6 py-3 border-2 border-brand-dark text-accent-navy rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-accent-navy transition-all"
            >
              {step === 1 ? 'Cancel' : 'Back'}
            </button>
            {step < 3 ? (
              <button
                onClick={() => {
                  if (step === 1 && !emergencyType) { setError('Please select an emergency type.'); return; }
                  setError(''); setStep(s => s + 1);
                }}
                className="flex items-center gap-2 px-8 py-3 bg-red-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-red-600 transition-all shadow-lg"
              >
                Continue <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={`flex items-center gap-2 px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  canSubmit
                    ? 'bg-red-500 text-white hover:bg-red-600 shadow-lg shadow-red-200'
                    : 'bg-brand-dark text-text-muted cursor-not-allowed'
                }`}
              >
                {submitting
                  ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <><SafeIcon icon={FiShield} className="w-4 h-4" /> Submit Emergency Request</>
                }
              </button>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default DocuVaultEmergencyReleaseModal;